// SF-Symbol-style line icons for the catalog. 24×24 viewbox, 1.75 stroke,
// rounded caps and joins, stroke="currentColor" so each icon inherits the
// surrounding text color. These replace the colored dots so each item kind
// reads as a recognizable glyph on an iPad across the floor.

type IconProps = { className?: string | undefined; "aria-hidden"?: boolean | undefined };

function Svg({ children, className, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon, aria-hidden
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={rest["aria-hidden"] ?? true}
      className={className}
    >
      {children}
    </svg>
  );
}

function PistolIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 8h11l2 3h3l-1 4h-3l-2 2h-4l-1-2H6l-2-2V8z" />
      <path d="M8 15v3h4l1-2" />
    </Svg>
  );
}

function RifleIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 11h15l3-2v4l-3-1H3z" />
      <path d="M5 13l-1 4h3l1-4" />
      <path d="M12 11v2" />
    </Svg>
  );
}

function BottleIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10 3h4v3l1 2v10a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V8l1-2V3z" />
      <path d="M10 11h4" />
    </Svg>
  );
}

function WrenchIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M15 4a4 4 0 0 1 4 5l-9 9a3 3 0 0 1-4-4l9-9c1-.5 2 0 2 0z" />
      <circle cx="7" cy="17" r="1" />
    </Svg>
  );
}

function TagIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 11V3h8l10 10-8 8L3 11z" />
      <circle cx="7.5" cy="7.5" r="1.25" />
    </Svg>
  );
}

function GiftIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="9" width="18" height="4" rx="1" />
      <path d="M5 13v8h14v-8M12 9v12" />
      <path d="M8 9a2.5 2.5 0 1 1 4-2 2.5 2.5 0 1 1 4 2" />
    </Svg>
  );
}

function TicketIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 8V5h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4z" />
      <path d="M12 6v3M12 12v3" />
    </Svg>
  );
}

function BoxIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 7l9-4 9 4-9 4-9-4z" />
      <path d="M3 7v10l9 4 9-4V7" />
      <path d="M12 11v10" />
    </Svg>
  );
}

function MinusIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12h8" />
    </Svg>
  );
}

export function KindIcon({ kind, className }: { kind: string; className?: string | undefined }) {
  switch (kind) {
    case "serialized_firearm":
      // Pistol vs rifle is on the firearm attributes, not on kind. Default
      // to pistol glyph; the detail view carries the exact ATF type.
      return <PistolIcon className={className} />;
    case "serialized_other":
      return <TagIcon className={className} />;
    case "standard":
      return <BottleIcon className={className} />;
    case "service":
      return <WrenchIcon className={className} />;
    case "fee":
      return <TagIcon className={className} />;
    case "gift_card":
      return <GiftIcon className={className} />;
    case "class_seat":
      return <TicketIcon className={className} />;
    case "kit":
      return <BoxIcon className={className} />;
    case "non_inventory":
      return <MinusIcon className={className} />;
    default:
      return <TagIcon className={className} />;
  }
}

// Icon picker that honors ATF type for firearms (shows rifle for long guns).
export function FirearmTypeIcon({
  atfType,
  className,
}: {
  atfType: string;
  className?: string | undefined;
}) {
  if (atfType === "rifle" || atfType === "shotgun" || atfType === "sbr" || atfType === "sbs") {
    return <RifleIcon className={className} />;
  }
  return <PistolIcon className={className} />;
}

export function ChevronRightIcon({ className }: { className?: string | undefined }) {
  return (
    <svg
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M1 1l6 6-6 6" />
    </svg>
  );
}

export function BackChevronIcon({ className }: { className?: string | undefined }) {
  return (
    <svg
      width="11"
      height="19"
      viewBox="0 0 11 19"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M10 1L1 9.5l9 8.5" />
    </svg>
  );
}

export function SearchIcon({ className }: { className?: string | undefined }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="7" cy="7" r="5" />
      <path d="M11 11l3 3" />
    </svg>
  );
}

export function LockIcon({ className }: { className?: string | undefined }) {
  return (
    <Svg className={className}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  );
}

export function AgeIcon({ className }: { className?: string | undefined }) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </Svg>
  );
}

export function ScanIcon({ className }: { className?: string | undefined }) {
  // Barcode viewfinder — four corner brackets around a run of vertical bars,
  // SF-Symbol house style.
  return (
    <Svg className={className}>
      <path d="M4 8V5a1 1 0 0 1 1-1h3" />
      <path d="M20 8V5a1 1 0 0 0-1-1h-3" />
      <path d="M4 16v3a1 1 0 0 0 1 1h3" />
      <path d="M20 16v3a1 1 0 0 1-1 1h-3" />
      <path d="M8 8v8M11 8v8M14 8v8M17 8v8" />
    </Svg>
  );
}
